import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { SlotPicker } from '../../components/booking/SlotPicker';
import { BookingShell, BookingSummary } from '../../components/booking/BookingShell';
import { detectTimezone } from '../../lib/timezone';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ManageBooking() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState(null);
  const [mode, setMode] = useState(params.get('action') === 'cancel' ? 'cancel' : 'view');
  const [tz, setTz] = useState(detectTimezone());
  const [slots, setSlots] = useState([]);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/booking/${token}`).then((r) => setBooking(r.data))
      .catch((e) => setError(e.response?.data?.detail || 'Booking not found'));
  }, [token]);

  const startReschedule = async () => {
    setMode('reschedule'); setSelected(null);
    const r = await axios.get(`${API}/public/booking/${token}/slots`);
    setSlots(r.data.slots);
  };
  const act = async (path, body) => {
    setBusy(true); setNotice(null);
    try {
      const r = await axios.post(`${API}/public/booking/${token}/${path}`, body);
      setBooking(r.data); setMode('view');
      setNotice(path === 'cancel' ? 'Your booking has been cancelled.' : 'Your booking has been moved.');
    } catch (e) {
      setNotice(e.response?.data?.detail || 'Something went wrong.');
      if (path === 'reschedule') startReschedule();
    } finally { setBusy(false); }
  };

  if (error) return <BookingShell><p className="text-center py-10 text-slate-600" data-testid="manage-error">{error}</p></BookingShell>;
  if (!booking) return <BookingShell><div className="py-16 text-center"><Loader2 className="animate-spin inline" /></div></BookingShell>;

  const heading = booking.status === 'cancelled' ? 'Booking cancelled' : 'Your booking';
  return (
    <BookingShell>
      {notice && <p className="text-center text-sm mb-4 text-[#1a3a5c] font-medium" data-testid="manage-notice">{notice}</p>}
      {mode === 'reschedule' ? (
        <div className="space-y-4">
          <h2 className="text-base md:text-lg font-semibold">Pick a new time</h2>
          <SlotPicker slots={slots} tz={tz} onTzChange={setTz} selected={selected} onSelect={setSelected} />
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setMode('view')} data-testid="reschedule-back">Back</Button>
            <Button disabled={!selected || busy} onClick={() => act('reschedule', { starts_at: selected.starts_at })} className="bg-[#1a3a5c]" data-testid="reschedule-confirm">
              {busy ? <Loader2 className="animate-spin" size={16} /> : 'Confirm new time'}
            </Button>
          </div>
        </div>
      ) : (
        <BookingSummary booking={booking} tz={tz} heading={heading} manage>
          {booking.can_change ? (
            mode === 'cancel' ? (
              <div className="border border-red-200 bg-red-50 rounded-lg p-4 space-y-3" data-testid="cancel-confirm-box">
                <p className="text-sm">Cancel this meeting?</p>
                <div className="flex gap-2 justify-center">
                  <Button variant="outline" onClick={() => setMode('view')} data-testid="cancel-keep">Keep it</Button>
                  <Button variant="destructive" disabled={busy} onClick={() => act('cancel')} data-testid="cancel-confirm">Yes, cancel</Button>
                </div>
              </div>
            ) : (
              <div className="flex gap-2 justify-center">
                <Button variant="outline" onClick={startReschedule} data-testid="manage-reschedule-btn">Reschedule</Button>
                <Button variant="outline" className="text-red-600" onClick={() => setMode('cancel')} data-testid="manage-cancel-btn">Cancel</Button>
              </div>
            )
          ) : booking.status !== 'cancelled' && (
            <p className="text-xs text-slate-500" data-testid="manage-locked">Changes are closed within {booking.min_notice_hours} hours of the meeting. Please reply to your confirmation email.</p>
          )}
        </BookingSummary>
      )}
    </BookingShell>
  );
}
